/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

/**
 * LLM providers and their models offered for AI agents (edit now, create later).
 * `value` is what the Agentic AI API stores (`provider` / `model`).
 * Keep in sync with server/src/ai-agents/ai-agents.constants.ts (AI_AGENT_PROVIDERS).
 */
export const AI_AGENT_PROVIDERS = [
  {
    value: 'openai',
    label: 'OpenAI',
    models: [
      {
        value: 'gpt-4o-mini',
        label: 'gpt-4o-mini',
        description: 'Sub-second TTFT, highly cost-efficient. Recommended for smart glass audio and real-time voice TTS.',
      },
      {
        value: 'gpt-4o',
        label: 'gpt-4o',
        description: 'High accuracy on complex PDF tables, service diagrams, and reliable multi-turn MCP tool calling.',
      },
      {
        value: 'gpt-5.6-luna',
        label: 'gpt-5.6-luna',
        description: 'Advanced enterprise comprehension across extensive multi-manual technical documentation.',
      },
      {
        value: 'o3-mini',
        label: 'o3-mini',
        description: 'Step-by-step diagnostic reasoning for complex fault codes, logic trees, and technical troubleshooting.',
      },
    ],
  },
];

/**
 * Agent types. `value` is what the Agentic AI API stores (`type`).
 * Keep in sync with server/src/ai-agents/ai-agents.constants.ts (AI_AGENT_TYPES).
 */
export const AI_AGENT_TYPES = [
  { value: 'main', label: 'Main' },
];

// Catalogue label, or a readable fallback for unlisted types ("sub_agent" -> "Sub Agent").
export const getAiAgentTypeLabel = (value) =>
  AI_AGENT_TYPES.find((t) => t.value === value)?.label
  || (value || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const findAiAgentProvider = (value) =>
  AI_AGENT_PROVIDERS.find((p) => p.value === value) || null;

export const getAiAgentProviderLabel = (value) =>
  findAiAgentProvider(value)?.label || value || '';

export const getAiAgentModels = (providerValue) =>
  findAiAgentProvider(providerValue)?.models || [];

/**
 * Account-level AgenticAI host (account metadata `aiAgent.host`). Empty means
 * the server's AGENTICAI_HOST is used. Mirrors AiAgentSettingsDto on the server.
 */
export const AI_AGENT_HOST_MAX_LENGTH = 255;

// Error message for an invalid host, or null when valid (empty is valid).
export const validateAiAgentHost = (value) => {
  const host = (value || '').trim();
  if (!host) { return null; }
  if (!/^https?:\/\/[^\s/?#]+\S*$/i.test(host)) {
    return 'AgenticAI host must be a valid URL starting with http:// or https://';
  }
  return null;
};
